import { Squad, SquadPlayer } from '../types';
import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../../contexts/strings';
import { useCallback } from 'react';
import { createSquadPlayer } from './player';
import { useUserAuth } from '../../../../contexts/auth';
import adminApi from '../../../../api/admin-api';

export const useSquad = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const { currentUser } = useUserAuth();

  const createSquad = useCallback(
    async (teamId: string, season: number, playerIds: string) => {
      try {
        const response = await adminApi.post({
          path: `/squad/${teamId}/${season}`,
          auth: await currentUser?.getIdToken(),
          body: {
            players: playerIds,
          },
        });

        if (!response.ok) {
          throw new Error(`Creating squad failed! (${response.status})`);
        }
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
        throw error;
      }
    },
    [enqueueSnackbar, strings, currentUser]
  );

  const fetchSquad = useCallback(
    async (
      teamId: string,
      season: number,
      full = true,
      skipCache = true
    ): Promise<SquadPlayer[] | null> => {
      let squadPlayers: SquadPlayer[] | null = null;

      try {
        const response = await adminApi.get({
          path: `/squad/${teamId}/${season}?full=${full}&skipCache=${skipCache}`,
          auth: await currentUser?.getIdToken(),
        });

        if (response.status === 404) {
          return [];
        }

        if (!response.ok) {
          throw new Error(`Fetching squad failed! (${response.status})`);
        }

        const squad: Squad = await response.json();

        if (squad?.squad) {
          squadPlayers = squad.squad.map((player) => createSquadPlayer(teamId, player));
        }
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.loadingError, {
          variant: 'error',
        });
        throw error;
      }

      return squadPlayers;
    },
    [enqueueSnackbar, strings, currentUser]
  );

  return { fetchSquad, createSquad };
};
